import GenericAction from '@/components/actions/GenericAction.tsx'
import {
  Copy,
  DownloadCloud,
  Blinds,
  SquareArrowOutUpRight,
  FoldVertical,
  UnfoldVertical,
} from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useToast } from '@/components/ui/use-toast'
import { getOutputVersion } from '@/lib/action-utils'
import { cn } from '@/lib/utils.ts'
import { getMaxHeightClass } from '@/lib/dynamic-classes.ts'

const COLLAPSED_HEIGHT_REM = 19

export default function AnalyzeWebPageStructureAction({
  action,
  isLast = false,
  isInteractive = false,
  collapsedHeightRem = COLLAPSED_HEIGHT_REM,
}: {
  action: AnalyzeWebPageStructureActionPayload
  isLast?: boolean
  isInteractive?: boolean
  collapsedHeightRem?: string
}) {
  const terminalContentRef = useRef(null)
  const contentRef = useRef(null)
  const buttonRef = useRef(null)
  const topRef = useRef(null)
  const [showTerminalOutputs, setShowTerminalOutputs] = useState(false)

  const isInView = element => {
    if (!element) return false
    // Get distance from element to top of viewport
    const rect = element.getBoundingClientRect()
    // Simple check if it's in the viewport
    return rect.top >= 0 && rect.top <= window.innerHeight
  }

  const comment = action.args.comment
  const url = action.args.website_url
  const output = (action.outputs ?? []).find(isSuccessOutput)
  const isDone = action.status === 'DONE'
  const outputContent = getOutputVersion(output) === 2 ? output?.content : output
  let annotated_screenshot
  try {
    const pageStructure = JSON.parse(outputContent.page_structure)
    annotated_screenshot = pageStructure.annotated_screenshot
  } catch (error) {
    console.error(error)
  }

  const hasImage = Boolean(annotated_screenshot?.public_url_large)

  const handleToggle = () => {
    setShowTerminalOutputs(prev => !prev)

    requestAnimationFrame(() => {
      if (!showTerminalOutputs && !isInView(buttonRef.current)) {
        buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      } else if (showTerminalOutputs && !isInView(topRef.current)) {
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }

  const shouldShowDownloadButton = isDone && outputContent && hasImage

  return (
    <GenericAction
      title={comment ?? 'Analyze web page structure'}
      action={action}
      detailsContent={comment}
      actionIcon={Blinds}
      isLast={isLast}
      isInteractive={isInteractive}
    >
      <div ref={topRef} />
      <div ref={contentRef} className="w-full">
        <div className="group relative flex w-full flex-col gap-2">
          <BrowserWindow>
            <AddressBar
              url={url}
              imageUrl={annotated_screenshot?.public_url_original}
              shouldShowDownloadButton={shouldShowDownloadButton}
            />
            <div
              ref={terminalContentRef}
              className={`flex w-full max-w-full flex-col whitespace-pre-wrap break-all rounded-b-sm border border-border bg-base-background font-dm-mono text-sm font-normal leading-tight ${
                !showTerminalOutputs && `${getMaxHeightClass(collapsedHeightRem)} overflow-hidden`
              }`}
            >
              {isDone && outputContent && hasImage && (
                <img
                  src={annotated_screenshot?.public_url_large}
                  alt={`Screenshot of ${url}`}
                  loading="lazy"
                  ref={terminalContentRef}
                />
              )}
            </div>
          </BrowserWindow>
          <Button
            ref={buttonRef}
            className="inline-flex h-8 cursor-pointer items-center justify-center gap-2 rounded-md bg-stone-100 px-3 py-1.5 text-stone-900"
            variant="secondary"
            onClick={handleToggle}
          >
            {showTerminalOutputs ? (
              <FoldVertical className="relative size-4" />
            ) : (
              <UnfoldVertical className="relative size-4" />
            )}

            <div className="font-['Inter'] text-sm font-medium leading-tight ">
              {showTerminalOutputs ? `Collapse image` : `Expand image`}
            </div>
          </Button>
        </div>
      </div>
    </GenericAction>
  )
}

export const BrowserWindow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'divide-y divide-border overflow-hidden rounded border border-border',
        className
      )}
    >
      {children}
    </div>
  )
}

export const AddressBar: React.FC<{
  url: string
  imageUrl: string
  shouldShowDownloadButton?: boolean
  children?: React.ReactNode
}> = ({ url, imageUrl, children, shouldShowDownloadButton = false }) => {
  const toast = useToast()
  const copyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.toast({ title: 'URL copied to clipboard' })
    } catch (_err) {
      // no access to clipboard
      toast.toast({ title: 'Could not copy the URL', variant: 'destructive' })
    }
  }, [url])

  return (
    <div className="grid grid-cols-[max-content,1fr,max-content] place-items-center gap-2 bg-base-background p-1 text-base-muted-foreground">
      <div className="ml-3 flex gap-1 *:size-1.5 *:rounded-full *:bg-border">
        <span />
        <span />
        <span />
      </div>
      <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-nowrap font-pp-supply-sans">
        {url}
      </p>
      <div>
        {children}
        {shouldShowDownloadButton && (
          <Button size="sm" variant="ghost" asChild className="w-8">
            <a href={imageUrl} target="_blank" rel="noreferrer">
              <DownloadCloud className="size-4" />
            </a>
          </Button>
        )}
        <Button size="sm" variant="ghost" asChild className="w-8">
          <a href={url} target="_blank" rel="noreferrer">
            <SquareArrowOutUpRight className="size-4" />
          </a>
        </Button>
        <Button size="sm" variant="ghost" onClick={copyUrl} className="w-8">
          <Copy className="size-4" />
        </Button>
      </div>
    </div>
  )
}

type AnalyzeWebPageStructureActionPayload = {
  actionId: string
  endCommitHash: string | null
  args: {
    website_url: string
    comment: string
  }
  startCommitHash: string | null
  elementId: string
  stepId: string
  startSnapshotHash: string | null
  name: string
  subtype: string | null
  index: number
  iterationId: string
  hideArgsFromPrompt: boolean
  id: string
  endSnapshotHash: string | null
  type: string
  status: string
  outputs: GenerateImageActionOutput[]
}

type FailedOutput_v1 = {
  error: unknown
  image_path: null
  screenshot_public_url_large?: null
  screenshot_public_url_small?: null
  screenshot_public_url_original?: null
}

type SuccessOutput_v1 = {
  pageStructure: string
}

type GenerateImageActionOutput_v1 = FailedOutput_v1 | SuccessOutput_v1

type FailedOutput = {
  content: null
  status: 'failed'
}

type SuccessOutput = {
  content: SuccessOutput_v1
  status: 'success'
}

type CurrentOutput = FailedOutput | SuccessOutput

type GenerateImageActionOutput = GenerateImageActionOutput_v1 | CurrentOutput

function isSuccessOutput(output: GenerateImageActionOutput) {
  if (getOutputVersion(output) === 2) {
    return output?.status === 'success'
  } else {
    return output?.error === null
  }
}
